import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { getConsultantCalendar } from "../../../api/consultantPortalApi";
import { PageHeader, Panel, MiniIconButton } from "../../../components/Portal/ui";

// Day-type chrome aligned to the Corporate Azure palette: mint for recorded
// activity, navy/blue for public holidays, amber for leave.
const DAY_TYPE_BG = {
  leave: "#FDF1E3",
  public_holiday: "#EAF2F8",
  activity_report: "#E8F8F5",
};

const DAY_TYPE_BORDER = {
  leave: "#E8A55B",
  public_holiday: "#9CBBD6",
  activity_report: "#71D4C5",
};

const LEGEND = [
  { key: "workDay", bg: "white", border: "portal.line" },
  { key: "leaveDay", bg: DAY_TYPE_BG.leave, border: DAY_TYPE_BORDER.leave },
  { key: "publicHoliday", bg: DAY_TYPE_BG.public_holiday, border: DAY_TYPE_BORDER.public_holiday },
  { key: "weekend", bg: "portal.soft", border: "portal.line" },
  { key: "today", bg: "white", border: "portal.primary", thick: true },
];

const Calendar = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getConsultantCalendar(year, month);
      setCalendarData(response);
    } catch (error) {
      toast({
        title: t("consultantPortal.calendar.toasts.loadFailedTitle"),
        description:
          error?.response?.data?.detail ||
          t("consultantPortal.calendar.toasts.loadFailedDescription"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [year, month, toast, t]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const goToPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  const monthName = useMemo(() => {
    const names = t("consultantPortal.common.monthNames", { returnObjects: true });
    return Array.isArray(names) ? names[month - 1] || "" : "";
  }, [t, month]);

  const dayNames = useMemo(() => {
    const names = t("consultantPortal.calendar.dayNames", { returnObjects: true });
    return Array.isArray(names) ? names : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  }, [t]);

  // Build calendar grid
  const calendarGrid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    // Monday = 0, Sunday = 6
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const daysByDate = {};
    if (calendarData?.days) {
      for (const entry of calendarData.days) {
        const key = entry.date;
        if (!daysByDate[key]) daysByDate[key] = [];
        daysByDate[key].push(entry);
      }
    }

    const today = new Date();
    const todayStr =
      today.getFullYear() === year && today.getMonth() + 1 === month
        ? `${year}-${String(month).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`
        : null;

    const cells = [];
    for (let i = 0; i < startDow; i++) {
      cells.push({ key: `empty-${i}`, empty: true });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dateObj = new Date(year, month - 1, day);
      const dow = dateObj.getDay();
      const isWeekend = dow === 0 || dow === 6;
      const isToday = dateStr === todayStr;
      const entries = daysByDate[dateStr] || [];
      cells.push({ key: dateStr, day, dateStr, isWeekend, isToday, entries });
    }
    return cells;
  }, [year, month, calendarData]);

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CP"
        eyebrow={t("consultantPortal.common.eyebrow", "Consultant Portal")}
        title={t("consultantPortal.calendar.pageTitle")}
      />

      <Box p="20px">
        <Panel
          mb="12px"
          title={`${monthName} ${year}`}
          actions={
            <HStack spacing="6px">
              <MiniIconButton
                icon={<FaChevronLeft />}
                aria-label="Previous month"
                onClick={goToPrevMonth}
              />
              <MiniIconButton
                icon={<FaChevronRight />}
                aria-label="Next month"
                onClick={goToNextMonth}
              />
              <Button variant="secondary" size="sm" onClick={goToToday}>
                {t("consultantPortal.calendar.today")}
              </Button>
            </HStack>
          }
        >
          <Box p="14px">
            {loading ? (
              <Flex minH="300px" align="center" justify="center">
                <Spinner size="lg" color="portal.primary" />
              </Flex>
            ) : (
              <Box>
                <Grid templateColumns="repeat(7, 1fr)" gap="6px" mb="6px">
                  {dayNames.map((name, idx) => (
                    <GridItem key={name} textAlign="center">
                      <Text
                        fontSize="10.5px"
                        fontWeight="900"
                        letterSpacing="0.055em"
                        color={idx >= 5 ? "#9DB0BF" : "portal.muted"}
                        textTransform="uppercase"
                        sx={{ marginBottom: "0 !important" }}
                      >
                        {name}
                      </Text>
                    </GridItem>
                  ))}
                </Grid>

                <Grid templateColumns="repeat(7, 1fr)" gap="6px">
                  {calendarGrid.map((cell) => {
                    if (cell.empty) {
                      return <GridItem key={cell.key} minH="64px" />;
                    }

                    const primaryEntry = cell.entries.find(
                      (e) => e.type === "public_holiday" || e.type === "leave"
                    );
                    const bgColor = cell.isWeekend
                      ? "portal.soft"
                      : primaryEntry
                      ? DAY_TYPE_BG[primaryEntry.type]
                      : "white";
                    const borderColor = cell.isToday
                      ? "portal.primary"
                      : primaryEntry
                      ? DAY_TYPE_BORDER[primaryEntry.type]
                      : "portal.line";

                    const tooltipLines = cell.entries.map((e) => e.label).filter(Boolean);

                    const cellContent = (
                      <GridItem
                        key={cell.key}
                        minH="64px"
                        bg={bgColor}
                        borderWidth={cell.isToday ? "2px" : "1px"}
                        borderColor={borderColor}
                        borderRadius="10px"
                        p="6px"
                        position="relative"
                      >
                        <Text
                          fontSize="12px"
                          fontWeight={cell.isToday ? "900" : "700"}
                          color={cell.isWeekend ? "#9DB0BF" : "portal.text"}
                          sx={{ marginBottom: "0 !important" }}
                        >
                          {cell.day}
                        </Text>
                        {primaryEntry && (
                          <Text
                            fontSize="10px"
                            fontWeight="700"
                            color="portal.muted"
                            noOfLines={2}
                            lineHeight="1.2"
                            mt="2px"
                            sx={{ marginBottom: "0 !important" }}
                          >
                            {primaryEntry.label}
                          </Text>
                        )}
                      </GridItem>
                    );

                    if (tooltipLines.length > 0) {
                      return (
                        <Tooltip
                          key={cell.key}
                          label={tooltipLines.join("\n")}
                          whiteSpace="pre-wrap"
                          placement="top"
                          hasArrow
                        >
                          {cellContent}
                        </Tooltip>
                      );
                    }
                    return cellContent;
                  })}
                </Grid>
              </Box>
            )}
          </Box>
        </Panel>

        <Panel title={t("consultantPortal.calendar.legend")}>
          <Flex gap="20px" wrap="wrap" p="14px">
            {LEGEND.map((entry) => (
              <HStack key={entry.key} spacing="8px">
                <Box
                  w="16px"
                  h="16px"
                  bg={entry.bg}
                  borderWidth={entry.thick ? "2px" : "1px"}
                  borderColor={entry.border}
                  borderRadius="6px"
                />
                <Text fontSize="12px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                  {t(`consultantPortal.calendar.${entry.key}`)}
                </Text>
              </HStack>
            ))}
          </Flex>
        </Panel>
      </Box>
    </Box>
  );
};

export default Calendar;
